import {
  Component,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { HasPermissionDirective } from '../../core/directives/has-permission.directive';
import {
  BulkBuAction,
  Member,
  MemberScope,
  MemberService,
  MemberStatus
} from '../../core/services/member.service';
import { PaymentService, PaymentMonthlySummary, BulkPaymentResult, Payment } from '../../core/services/payment.service';
import { TenantContextService } from '../../core/services/tenant-context.service';
import { AuthService } from '../../core/services/auth.service';
import { BusinessUnitStore } from '../../core/stores/business-unit.store';
import { MemberFormComponent } from './member-form/member-form.component';
import { BulkAssignBuModalComponent, BulkAssignResult } from './bulk-assign-bu-modal/bulk-assign-bu-modal.component';
import { BulkPayModalComponent } from './bulk-pay-modal/bulk-pay-modal.component';
import { MemberQuickViewDrawerComponent } from './member-quick-view-drawer/member-quick-view-drawer.component';
import { PaymentFormComponent } from '../payments/payment-form/payment-form.component';
import { TempPasswordModalComponent, CredentialsDelivered } from '../../shared/components/temp-password-modal/temp-password-modal.component';
import { ManageRolesModalComponent } from '../../shared/components/manage-roles-modal/manage-roles-modal.component';
import { ManageCommitteesModalComponent } from '../../shared/components/manage-committees-modal/manage-committees-modal.component';
import { ToastService } from '../../core/services/toast.service';
import { LoaderComponent } from '../../shared/components/loader/loader.component';
import { SpinnerComponent } from '../../shared/components/spinner/spinner.component';
import { NoBuSelectedComponent } from '../../shared/components/no-bu-selected/no-bu-selected.component';
import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';
import { RecycleBinModalComponent } from '../../shared/components/recycle-bin-modal/recycle-bin-modal.component';
import { EntitlementService } from '../../core/services/entitlement.service';
import { PlanLimitModalService } from '../../core/services/plan-limit-modal.service';
import { isGatedErrorCode } from '../../core/utils/entitlement-labels';

export type FeeFilter = '' | 'paid' | 'pending' | 'inactive';

@Component({
  selector: 'app-members',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HasPermissionDirective,
    MemberFormComponent,
    BulkAssignBuModalComponent,
    BulkPayModalComponent,
    MemberQuickViewDrawerComponent,
    PaymentFormComponent,
    TempPasswordModalComponent,
    ManageRolesModalComponent,
    ManageCommitteesModalComponent,
    LoaderComponent,
    SpinnerComponent,
    NoBuSelectedComponent,
    ConfirmDialogComponent,
    RecycleBinModalComponent
  ],
  templateUrl: './members.component.html',
  styleUrls: ['./members.component.css']
})
export class MembersComponent implements OnInit, OnDestroy {
  private memberService = inject(MemberService);
  private paymentService = inject(PaymentService);
  private tenantContext = inject(TenantContextService);
  private auth = inject(AuthService);
  private buStore = inject(BusinessUnitStore);
  private toast = inject(ToastService);
  private router = inject(Router);
  private entitlement = inject(EntitlementService);
  private planLimitModal = inject(PlanLimitModalService);

  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  members: Member[] = [];
  totalCount = 0;
  totalPages = 0;
  currentPage = 1;
  pageSize = 20;

  isLoading = false;
  errorMessage = '';
  infoMessage = '';

  searchTerm = '';
  filterStatus: '' | MemberStatus = '';

  // Fee period + fee filter
  feeMonth = new Date().getMonth() + 1;
  feeYear = new Date().getFullYear();
  feeFilter: FeeFilter = '';

  feeSummary: PaymentMonthlySummary | null = null;
  isLoadingSummary = false;

  // Pay modal (single)
  showPayModal = false;
  payInitialMember: Member | null = null;
  payInitialMonth: number | null = null;
  payInitialYear: number | null = null;
  payInitialAmount: number | null = null;

  // Bulk pay modal
  showBulkPayModal = false;
  bulkPayMembers: Member[] = [];

  // Quick view drawer
  showQuickView = false;
  quickViewMember: Member | null = null;

  readonly months = [
    { value: 1, name: 'Jan' }, { value: 2, name: 'Feb' }, { value: 3, name: 'Mar' },
    { value: 4, name: 'Apr' }, { value: 5, name: 'May' }, { value: 6, name: 'Jun' },
    { value: 7, name: 'Jul' }, { value: 8, name: 'Aug' }, { value: 9, name: 'Sep' },
    { value: 10, name: 'Oct' }, { value: 11, name: 'Nov' }, { value: 12, name: 'Dec' }
  ];
  feeYears: number[] = [];

  // Modal state
  showFormModal = false;
  editingMember: Member | null = null;

  // Delete confirmation
  pendingDeleteMember: Member | null = null;
  isDeleting = false;

  // Inline single-member BU assignment
  pendingAssignMember: Member | null = null;
  isAssigning = false;

  // Temp password modal
  tempPasswordModal: {
    open: boolean;
    password: string;
    username: string | null;
    phone: string | null;
    email: string | null;
    credentialsDelivered: CredentialsDelivered | null;
    heading: string;
    subheading: string;
    footer: string;
  } = {
    open: false, password: '', username: null, phone: null, email: null,
    credentialsDelivered: null, heading: '', subheading: '', footer: ''
  };

  // Reset password confirmation
  resetConfirmMember: Member | null = null;
  isResetting = false;

  // Manage roles modal
  manageRolesModal: { open: boolean; member: Member | null } = { open: false, member: null };

  // Manage committees modal (Super Admin only)
  manageCommitteesModal: { open: boolean; member: Member | null } = { open: false, member: null };

  // Per-row action loading
  rowBusyId: string | null = null;

  // Quick-view drawer per-action loading
  drawerBusy = { reset: false, invite: false, deactivate: false, createUser: false };

  // Scope + selection
  readonly scope = signal<MemberScope>('assigned');
  readonly selectedIds = signal<Set<string>>(new Set());

  // Bulk modal
  readonly bulkModalOpen = signal(false);
  readonly bulkModalAction = signal<BulkBuAction>('add');

  readonly selectedBusinessUnitId = this.tenantContext.selectedBusinessUnitId;

  committeeName(): string | null {
    return this.tenantContext.getCurrentTenantName();
  }
  readonly hasBuSelected = computed(() => this.selectedBusinessUnitId() != null);

  readonly isSuperAdmin = computed(() => this.auth.isSuperAdminSignal());
  readonly isTenantAdmin = computed(() => this.auth.isTenantAdminSignal());
  readonly isAdmin = computed(() => this.isSuperAdmin() || this.isTenantAdmin());
  readonly canSwitchScope = computed(() => !this.isSuperAdmin());

  // Recycle bin (SuperAdmin only)
  showRecycleBin = signal(false);

  // Fee reminder broadcast
  showFeeReminderPanel = false;
  reminderChannels = { email: true, sms: true };
  isSendingReminder = false;
  reminderMessage = '';
  reminderError = '';

  readonly selectedBuName = computed(() => {
    const id = this.selectedBusinessUnitId();
    if (id == null) return null;
    return this.buStore.byId().get(id)?.name ?? null;
  });

  readonly selectedCount = computed(() => this.selectedIds().size);

  constructor() {
    const cy = new Date().getFullYear();
    for (let i = cy - 5; i <= cy + 1; i++) this.feeYears.push(i);

    // Reload whenever the selected business unit changes after mount. effect() always runs
    // once on its first flush regardless of when it's created — tracking the previous value
    // explicitly (rather than an "initialised" flag set in ngOnInit) makes that first run a
    // real no-op instead of duplicating ngOnInit's initial load.
    let previousBuId = this.tenantContext.selectedBusinessUnitId();
    effect(() => {
      const buId = this.tenantContext.selectedBusinessUnitId();
      if (buId === previousBuId) return;
      previousBuId = buId;

      // When BU clears, fall back to "all"; tabs assigned/unassigned require a BU.
      if (buId == null && this.scope() !== 'all') {
        this.scope.set('all');
      }
      this.currentPage = 1;
      if (buId != null) {
        this.loadMembers();
        this.loadFeeSummary();
      }
    });
  }

  ngOnInit(): void {
    this.buStore.loadOnce();
    this.scope.set(this.computeInitialScope());
    if (this.hasBuSelected()) {
      this.loadMembers();
      this.loadFeeSummary();
    }

    this.searchSubject.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.currentPage = 1;
      this.loadMembers();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private computeInitialScope(): MemberScope {
    if (this.auth.isSuperAdmin()) return 'all';
    if (this.auth.isTenantAdmin()) return 'all';
    return this.hasBuSelected() ? 'assigned' : 'all';
  }

  setScope(s: MemberScope): void {
    if (!this.canSwitchScope()) return;
    if ((s === 'assigned' || s === 'unassigned') && !this.hasBuSelected()) return;
    if (this.scope() === s) return;
    this.scope.set(s);
    this.currentPage = 1;
    this.loadMembers();
  }

  loadMembers(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.infoMessage = '';

    this.memberService.getMembers({
      search: this.searchTerm || undefined,
      status: this.filterStatus || undefined,
      scope: this.scope(),
      page: this.currentPage,
      pageSize: this.pageSize,
      feeStatusMonth: this.feeMonth,
      feeStatusYear: this.feeYear,
      feeFilter: this.feeFilter || undefined
    }).subscribe({
      next: (res) => {
        if (res.success && res.data) {
          const raw = res.data as any;
          if (Array.isArray(raw)) {
            this.members = raw;
            this.totalCount = raw.length;
            this.totalPages = 1;
          } else {
            this.members = raw.data || [];
            this.totalCount = raw.totalCount || 0;
            this.totalPages = raw.totalPages || 0;
          }
          if (res.message) this.infoMessage = res.message;
          this.refreshQuickViewMember();
        } else {
          this.errorMessage = res.message || 'Failed to load members.';
          this.members = [];
          this.totalCount = 0;
          this.totalPages = 0;
        }
        this.isLoading = false;
      },
      error: (err) => {
        this.errorMessage = err?.error?.message || 'Failed to load members.';
        this.isLoading = false;
      }
    });
  }

  onSearchChange(): void {
    this.searchSubject.next(this.searchTerm);
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadMembers();
  }

  setFeeFilter(f: FeeFilter): void {
    if (this.feeFilter === f) return;
    this.feeFilter = f;
    this.currentPage = 1;
    this.loadMembers();
  }

  setFeePeriod(month: number, year: number): void {
    this.feeMonth = month;
    this.feeYear = year;
    this.currentPage = 1;
    this.loadMembers();
    this.loadFeeSummary();
  }

  shiftFeePeriod(delta: number): void {
    let m = this.feeMonth + delta;
    let y = this.feeYear;
    while (m < 1)  { m += 12; y -= 1; }
    while (m > 12) { m -= 12; y += 1; }
    this.setFeePeriod(m, y);
  }

  isCurrentPeriod(): boolean {
    const now = new Date();
    return this.feeMonth === now.getMonth() + 1 && this.feeYear === now.getFullYear();
  }

  goToCurrentPeriod(): void {
    const now = new Date();
    this.setFeePeriod(now.getMonth() + 1, now.getFullYear());
  }

  loadFeeSummary(): void {
    if (!this.hasBuSelected()) {
      this.feeSummary = null;
      return;
    }
    this.isLoadingSummary = true;
    this.paymentService.getMonthlySummary(this.feeMonth, this.feeYear).subscribe({
      next: (res) => {
        this.isLoadingSummary = false;
        if (res.success && res.data) {
          this.feeSummary = res.data;
        }
      },
      error: () => { this.isLoadingSummary = false; }
    });
  }

  get collectionRate(): number {
    const s = this.feeSummary;
    if (!s || s.totalExpected === 0) return 0;
    return Math.round((s.totalCollected / s.totalExpected) * 100);
  }

  monthLabel(m: number): string {
    return this.months.find(x => x.value === m)?.name ?? `${m}`;
  }

  /**
   * Tri-state fee status for the selected period:
   *  - 'inactive' = member status != Active
   *  - 'paid'     = totalPaid >= expected (and expected > 0)
   *  - 'partial'  = totalPaid > 0 but < expected
   *  - 'pending'  = no payments yet (or expected unknown)
   */
  feeStatusKind(member: Member): 'paid' | 'partial' | 'pending' | 'inactive' {
    if (member.status !== 'Active') return 'inactive';
    const expected = member.currentPeriodExpected ?? 0;
    const paid = member.currentPeriodTotalPaid ?? 0;
    if (expected > 0 && paid >= expected) return 'paid';
    if (paid > 0) return 'partial';
    return 'pending';
  }

  feeStatusBadgeClass(member: Member): string {
    switch (this.feeStatusKind(member)) {
      case 'paid':     return 'bg-green-50 text-green-700 ring-1 ring-green-200';
      case 'partial':  return 'bg-amber-50 text-amber-700 ring-1 ring-amber-200';
      case 'pending':  return 'bg-gray-100 text-gray-600 ring-1 ring-gray-200';
      case 'inactive': return 'bg-gray-100 text-gray-500 ring-1 ring-gray-200';
    }
  }

  feeStatusLabel(member: Member): string {
    switch (this.feeStatusKind(member)) {
      case 'paid':     return 'Paid';
      case 'partial':  return 'Partial';
      case 'pending':  return 'Pending';
      case 'inactive': return 'N/A';
    }
  }

  feeStatusTooltip(member: Member): string {
    if (member.status !== 'Active') return 'Inactive member';
    const expected = member.currentPeriodExpected ?? 0;
    const paid = member.currentPeriodTotalPaid ?? 0;
    if (expected <= 0) return paid > 0 ? `Paid LKR ${paid.toLocaleString()}` : 'No fee set';
    if (paid <= 0) return `Owes LKR ${expected.toLocaleString()}`;
    if (paid >= expected) {
      const surplus = paid - expected;
      return surplus > 0
        ? `Paid LKR ${paid.toLocaleString()} of ${expected.toLocaleString()} (over by ${surplus.toLocaleString()})`
        : `Paid LKR ${paid.toLocaleString()} of ${expected.toLocaleString()}`;
    }
    const short = expected - paid;
    return `Paid LKR ${paid.toLocaleString()} of ${expected.toLocaleString()} (short ${short.toLocaleString()})`;
  }

  /** Dynamic label for the row Pay button — adapts based on what's already been paid. */
  payButtonLabel(member: Member): string {
    const kind = this.feeStatusKind(member);
    if (kind === 'partial') return 'Top up';
    if (kind === 'paid') return 'Add payment';
    return 'Pay';
  }

  payButtonClass(member: Member): string {
    const kind = this.feeStatusKind(member);
    if (kind === 'paid') {
      return 'border-blue-200 text-blue-700 hover:bg-blue-50';
    }
    if (kind === 'partial') {
      return 'border-amber-200 text-amber-700 hover:bg-amber-50';
    }
    return 'border-blue-600 text-blue-700 bg-blue-50 hover:bg-blue-100';
  }

  /** Suggested prefill amount when opening the Pay modal: outstanding shortfall, else 0. */
  private payPrefillAmount(member: Member): number | null {
    const expected = member.currentPeriodExpected ?? member.monthlyFee ?? 0;
    const paid = member.currentPeriodTotalPaid ?? 0;
    const short = expected - paid;
    if (short > 0) return short;
    // Member is fully paid (or expected unknown) — leave amount blank so admin types it.
    return null;
  }

  // Pay actions
  openPayForMember(member: Member): void {
    if (member.status !== 'Active') return;
    this.payInitialMember = member;
    this.payInitialMonth = this.feeMonth;
    this.payInitialYear = this.feeYear;
    this.payInitialAmount = this.payPrefillAmount(member);
    this.showPayModal = true;
  }

  closePayModal(): void {
    this.showPayModal = false;
    this.payInitialMember = null;
    this.payInitialMonth = null;
    this.payInitialYear = null;
    this.payInitialAmount = null;
  }

  onPaymentSaved(_payment: Payment): void {
    this.toast.success('Payment recorded', _payment.receiptNo ? `Receipt ${_payment.receiptNo}` : undefined);
    this.closePayModal();
    this.loadMembers();
    this.loadFeeSummary();
  }

  // Bulk pay
  openBulkPay(): void {
    const ids = this.selectedIds();
    const selected = this.members.filter(m => m.id != null && ids.has(m.id));
    const eligible = selected.filter(m => m.status === 'Active');
    if (eligible.length === 0) {
      this.toast.error('No eligible members', 'Selected members must be Active to record payments.');
      return;
    }
    this.bulkPayMembers = eligible;
    this.showBulkPayModal = true;
  }

  closeBulkPayModal(): void {
    this.showBulkPayModal = false;
    this.bulkPayMembers = [];
  }

  onBulkPaySaved(result: BulkPaymentResult): void {
    this.toast.success(
      `Recorded ${result.successCount} payment(s)`,
      result.failureCount > 0
        ? `${result.failureCount} failed — see details.`
        : `Total Rs. ${result.totalCollected.toLocaleString()}`
    );
    this.closeBulkPayModal();
    this.clearSelection();
    this.loadMembers();
    this.loadFeeSummary();
  }

  openMemberDetail(member: Member): void {
    if (member.id == null) return;
    this.quickViewMember = member;
    this.showQuickView = true;
  }

  closeQuickView(): void {
    this.showQuickView = false;
    this.quickViewMember = null;
  }

  openFullProfile(member: Member): void {
    if (member.id == null) return;
    this.closeQuickView();
    this.router.navigate(['/members', member.id]);
  }

  /** Re-sync the drawer's bound member after a list reload so it reflects the latest record. */
  private refreshQuickViewMember(): void {
    const current = this.quickViewMember;
    if (!current || current.id == null) return;
    const updated = this.members.find(m => m.id === current.id) ?? null;
    this.quickViewMember = updated;
    if (!updated) this.showQuickView = false;
  }

  changePage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) return;
    this.currentPage = page;
    this.loadMembers();
  }

  get pages(): number[] {
    const arr: number[] = [];
    const start = Math.max(1, this.currentPage - 2);
    const end = Math.min(this.totalPages, start + 4);
    for (let i = start; i <= end; i++) arr.push(i);
    return arr;
  }

  get startIndex(): number {
    return this.totalCount === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
  }

  get endIndex(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalCount);
  }

  // Selection helpers
  isSelected(id: string | undefined): boolean {
    return id != null && this.selectedIds().has(id);
  }

  toggleSelect(id: string | undefined): void {
    if (id == null) return;
    const set = new Set(this.selectedIds());
    if (set.has(id)) set.delete(id); else set.add(id);
    this.selectedIds.set(set);
  }

  get allOnPageSelected(): boolean {
    const ids = this.members.map(m => m.id).filter((x): x is string => x != null);
    if (ids.length === 0) return false;
    const set = this.selectedIds();
    return ids.every(id => set.has(id));
  }

  toggleSelectAllOnPage(): void {
    const ids = this.members.map(m => m.id).filter((x): x is string => x != null);
    if (ids.length === 0) return;
    const set = new Set(this.selectedIds());
    if (ids.every(id => set.has(id))) {
      ids.forEach(id => set.delete(id));
    } else {
      ids.forEach(id => set.add(id));
    }
    this.selectedIds.set(set);
  }

  clearSelection(): void {
    this.selectedIds.set(new Set());
  }

  selectHousehold(member: Member): void {
    if (!member.householdId) return;
    const matchingIds = this.members
      .filter(m => m.householdId === member.householdId)
      .map(m => m.id)
      .filter((x): x is string => x != null);
    const set = new Set(this.selectedIds());
    matchingIds.forEach(id => set.add(id));
    this.selectedIds.set(set);
  }

  // Bulk modal
  openBulkAssign(action: BulkBuAction): void {
    if (this.selectedIds().size === 0) return;
    this.bulkModalAction.set(action);
    this.bulkModalOpen.set(true);
  }

  selectedMemberIds(): string[] {
    return Array.from(this.selectedIds());
  }

  onBulkSaved(result: BulkAssignResult): void {
    this.bulkModalOpen.set(false);
    this.infoMessage =
      `${result.action === 'add' ? 'Assigned' : result.action === 'remove' ? 'Removed' : 'Replaced'} ` +
      `committees for ${result.modifiedCount} member(s).` +
      (result.notFoundCount > 0 ? ` (${result.notFoundCount} not found)` : '');
    this.clearSelection();
    this.loadMembers();
  }

  closeBulkModal(): void {
    this.bulkModalOpen.set(false);
  }

  // Form
  openCreate(): void {
    if (!this.entitlement.withinLimit('members')) {
      const m = this.entitlement.metric('members');
      this.planLimitModal.showLimit({ limit: 'MaxMembers', current: m?.current, max: m?.max ?? null });
      return;
    }
    this.editingMember = null;
    this.showFormModal = true;
  }

  openEdit(member: Member): void {
    this.editingMember = member;
    this.showFormModal = true;
  }

  closeForm(): void {
    this.showFormModal = false;
    this.editingMember = null;
  }

  onMemberSaved(_member: Member): void {
    this.closeForm();
    this.loadMembers();
    this.loadFeeSummary();
  }

  confirmDelete(member: Member): void {
    if (member.id == null) return;
    this.pendingDeleteMember = member;
  }

  cancelDelete(): void {
    if (this.isDeleting) return;
    this.pendingDeleteMember = null;
  }

  performDelete(): void {
    const id = this.pendingDeleteMember?.id;
    if (id == null) return;
    this.isDeleting = true;
    this.memberService.deleteMember(id).subscribe({
      next: (res) => {
        this.isDeleting = false;
        if (res.success) {
          this.pendingDeleteMember = null;
          this.loadMembers();
        } else {
          this.errorMessage = res.message || 'Delete failed.';
        }
      },
      error: () => {
        this.isDeleting = false;
        this.errorMessage = 'Delete failed.';
      }
    });
  }

  pendingDeleteMemberName(): string {
    const m = this.pendingDeleteMember;
    if (!m) return '';
    return m.fullName || `Member #${m.id}`;
  }

  statusBadgeClass(status: string | undefined): string {
    switch (status) {
      case 'Active':   return 'bg-green-100 text-green-700';
      case 'Inactive': return 'bg-amber-100 text-amber-700';
      case 'Left':     return 'bg-gray-100 text-gray-600';
      default:         return 'bg-gray-100 text-gray-600';
    }
  }

  roleLabel(member: Member): string | null {
    const codes = member.userAccount?.roleCodes ?? [];
    if (codes.length === 0) return null;
    if (codes.includes(1)) return 'System Admin';
    if (codes.includes(2)) return 'Tenant Admin';
    if (codes.includes(5)) return 'Treasurer';
    if (codes.includes(10)) return 'Member';
    return `Role ${codes[0]}`;
  }

  roleBadgeClass(member: Member): string {
    const codes = member.userAccount?.roleCodes ?? [];
    if (codes.includes(1)) return 'bg-purple-100 text-purple-700 border border-purple-200';
    if (codes.includes(2)) return 'bg-indigo-100 text-indigo-700 border border-indigo-200';
    if (codes.includes(5)) return 'bg-emerald-100 text-emerald-700 border border-emerald-200';
    return 'bg-gray-100 text-gray-600 border border-gray-200';
  }

  buName(buId: string): string {
    return this.buStore.byId().get(buId)?.name ?? `BU ${buId}`;
  }

  isCurrentBu(buId: string): boolean {
    return this.selectedBusinessUnitId() === buId;
  }

  isMissingCurrentBu(member: Member): boolean {
    const buId = this.selectedBusinessUnitId();
    if (buId == null) return false;
    const ids = member.businessUnitIds ?? [];
    return !ids.includes(buId);
  }

  confirmAssign(member: Member): void {
    if (member.id == null) return;
    if (!this.isMissingCurrentBu(member)) return;
    this.pendingAssignMember = member;
  }

  cancelAssign(): void {
    if (this.isAssigning) return;
    this.pendingAssignMember = null;
  }

  pendingAssignMemberName(): string {
    const m = this.pendingAssignMember;
    if (!m) return '';
    return m.fullName || `Member #${m.id}`;
  }

  performAssign(): void {
    const member = this.pendingAssignMember;
    const buId = this.selectedBusinessUnitId();
    const buName = this.selectedBuName();
    if (!member || member.id == null || buId == null) return;
    this.isAssigning = true;
    this.rowBusyId = member.id;
    this.memberService.bulkAssignBusinessUnits({
      memberIds: [member.id],
      businessUnitIds: [buId],
      action: 'add'
    }).subscribe({
      next: (res) => {
        this.isAssigning = false;
        this.rowBusyId = null;
        if (res.success) {
          const ids = member.businessUnitIds ?? [];
          if (!ids.includes(buId)) {
            member.businessUnitIds = [...ids, buId];
          }
          this.pendingAssignMember = null;
          this.toast.success(
            'Member assigned',
            `${member.fullName} added to ${buName ?? 'this committee'}.`
          );
          this.loadFeeSummary();
        } else {
          this.toast.error('Assign failed', res.message || 'Could not assign member.');
        }
      },
      error: (err) => {
        this.isAssigning = false;
        this.rowBusyId = null;
        this.toast.error('Assign failed', err?.error?.message || 'Could not assign member.');
      }
    });
  }

  getInitials(name: string): string {
    const parts = (name || '').trim().split(' ').filter(p => p);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return (name || '?').substring(0, 2).toUpperCase();
  }

  onMemberFormUserProvisioned(evt: {
    tempPassword: string | null;
    username: string | null;
    credentialsDelivered: CredentialsDelivered | null;
    linked: boolean;
    reactivated: boolean;
  }): void {
    if (evt.linked) {
      const who = evt.username ? `Existing login for ${evt.username}` : 'Existing login';
      const detail = evt.reactivated
        ? `${who} reactivated and linked to this member. Use Reset password if they need new credentials.`
        : `${who} is now linked to this member. Use Reset password if they need new credentials.`;
      this.toast.success('Account linked', detail);
      return;
    }
    if (!evt.tempPassword) return;
    this.tempPasswordModal = {
      open: true,
      password: evt.tempPassword,
      username: evt.username,
      phone: null,
      email: null,
      credentialsDelivered: evt.credentialsDelivered,
      heading: 'Account ready',
      subheading: this.welcomeModalSubheading,
      footer: this.welcomeModalFooter
    };
  }

  closeTempPasswordModal(): void {
    this.tempPasswordModal = {
      open: false, password: '', username: null, phone: null, email: null,
      credentialsDelivered: null, heading: '', subheading: '', footer: ''
    };
  }

  private welcomeModalSubheading = 'A temporary password has been generated.';
  private welcomeModalFooter = 'Member will be asked to change this password on first login. They can log in with their phone number, NIC, or email.';

  createUserAccount(member: Member): void {
    if (member.id == null) return;
    if (!this.entitlement.withinLimit('users')) {
      const m = this.entitlement.metric('users');
      this.planLimitModal.showLimit({ limit: 'MaxUsersPerBu', current: m?.current, max: m?.max ?? null });
      return;
    }
    this.rowBusyId = member.id;
    this.drawerBusy = { ...this.drawerBusy, createUser: true };
    this.memberService.createUserAccount(member.id).subscribe({
      next: (res) => {
        this.rowBusyId = null;
        this.drawerBusy = { ...this.drawerBusy, createUser: false };
        if (res.success && res.data) {
          const linked = !!res.data.userAccountLinked;
          const reactivated = !!res.data.userAccountReactivated;
          if (linked) {
            const who = res.data.username ? `Existing login for ${res.data.username}` : 'Existing login';
            const detail = reactivated
              ? `${who} reactivated and linked to this member. Use Reset password if they need new credentials.`
              : `${who} is now linked to this member. Use Reset password if they need new credentials.`;
            this.toast.success('Account linked', detail);
          } else if (res.data.tempPassword) {
            this.tempPasswordModal = {
              open: true,
              password: res.data.tempPassword,
              username: res.data.username,
              phone: member.phone ?? null,
              email: member.email ?? null,
              credentialsDelivered: res.data.credentialsDelivered,
              heading: 'Account ready',
              subheading: this.welcomeModalSubheading,
              footer: this.welcomeModalFooter
            };
          }
          this.loadMembers();
        } else if (!isGatedErrorCode(res.code)) {
          this.toast.error('Could not create user account', res.message);
        }
      },
      error: (err) => {
        this.rowBusyId = null;
        this.drawerBusy = { ...this.drawerBusy, createUser: false };
        this.toast.error('Could not create user account', err?.error?.message || 'Please try again.');
      }
    });
  }

  resetMemberPassword(member: Member): void {
    if (member.id == null) return;
    this.resetConfirmMember = member;
  }

  cancelResetPassword(): void {
    if (this.isResetting) return;
    this.resetConfirmMember = null;
  }

  confirmResetPassword(): void {
    const member = this.resetConfirmMember;
    if (!member || member.id == null) return;
    this.isResetting = true;
    this.rowBusyId = member.id;
    this.drawerBusy = { ...this.drawerBusy, reset: true };
    this.memberService.resetMemberPassword(member.id).subscribe({
      next: (res) => {
        this.isResetting = false;
        this.rowBusyId = null;
        this.drawerBusy = { ...this.drawerBusy, reset: false };
        this.resetConfirmMember = null;
        if (res.success && res.data) {
          this.tempPasswordModal = {
            open: true,
            password: res.data.tempPassword,
            username: member.userAccount?.username ?? null,
            phone: member.phone ?? null,
            email: member.email ?? null,
            credentialsDelivered: res.data.credentialsDelivered,
            heading: 'Password reset',
            subheading: `${member.fullName}'s password has been reset.`,
            footer: 'Share this securely. They will be asked to change it on next login.'
          };
        } else {
          this.toast.error('Could not reset password', res.message);
        }
      },
      error: (err) => {
        this.isResetting = false;
        this.rowBusyId = null;
        this.drawerBusy = { ...this.drawerBusy, reset: false };
        this.resetConfirmMember = null;
        this.toast.error('Could not reset password', err?.error?.message || 'Please try again.');
      }
    });
  }

  inviteMember(member: Member): void {
    if (member.id == null) return;
    if (!this.entitlement.hasFeature('featureInvitations')) {
      this.planLimitModal.showFeature({ feature: 'FeatureInvitations' });
      return;
    }
    if (!this.entitlement.withinLimit('users')) {
      const m = this.entitlement.metric('users');
      this.planLimitModal.showLimit({ limit: 'MaxUsersPerBu', current: m?.current, max: m?.max ?? null });
      return;
    }
    this.rowBusyId = member.id;
    this.drawerBusy = { ...this.drawerBusy, invite: true };
    this.memberService.inviteMember(member.id).subscribe({
      next: (res) => {
        this.rowBusyId = null;
        this.drawerBusy = { ...this.drawerBusy, invite: false };
        if (res.success) {
          this.toast.success('Invitation sent', `Code: ${res.data?.invitationCode}`);
        } else if (!isGatedErrorCode(res.code)) {
          this.toast.error('Invitation failed', res.message);
        }
      },
      error: (err) => {
        this.rowBusyId = null;
        this.drawerBusy = { ...this.drawerBusy, invite: false };
        this.toast.error('Invitation failed', err?.error?.message || 'Please try again.');
      }
    });
  }

  openManageRoles(member: Member): void {
    if (!member.userAccount) return;
    this.manageRolesModal = { open: true, member };
  }

  closeManageRoles(): void {
    this.manageRolesModal = { open: false, member: null };
  }

  onRolesSaved(roleCodes: number[]): void {
    const m = this.manageRolesModal.member;
    if (!m || m.id == null) {
      this.closeManageRoles();
      return;
    }
    this.memberService.updateMemberUserRoles(m.id, roleCodes).subscribe({
      next: (res) => {
        if (res.success) {
          this.toast.success('Roles updated');
          this.closeManageRoles();
          this.loadMembers();
        } else {
          this.toast.error('Could not update roles', res.message);
          this.closeManageRoles();
        }
      },
      error: (err) => {
        this.toast.error('Could not update roles', err?.error?.message || 'Please try again.');
        this.closeManageRoles();
      }
    });
  }

  openManageBusinessUnits(member: Member): void {
    this.manageCommitteesModal = { open: true, member };
  }

  closeManageCommittees(): void {
    this.manageCommitteesModal = { open: false, member: null };
  }

  onCommitteesSaved(buIds: string[]): void {
    const m = this.manageCommitteesModal.member;
    if (!m || m.id == null) {
      this.closeManageCommittees();
      return;
    }
    this.memberService.bulkAssignBusinessUnits({
      memberIds: [m.id],
      businessUnitIds: buIds,
      action: 'replace'
    }).subscribe({
      next: (res) => {
        if (res.success) {
          this.toast.success('Committees updated');
          this.closeManageCommittees();
          this.loadMembers();
        } else {
          this.toast.error('Could not update committees', res.message);
          this.closeManageCommittees();
        }
      },
      error: (err) => {
        this.toast.error('Could not update committees', err?.error?.message || 'Please try again.');
        this.closeManageCommittees();
      }
    });
  }

  deactivateMemberUser(member: Member): void {
    if (member.id == null) return;
    this.rowBusyId = member.id;
    this.drawerBusy = { ...this.drawerBusy, deactivate: true };
    this.memberService.deactivateMemberUser(member.id).subscribe({
      next: (res) => {
        this.rowBusyId = null;
        this.drawerBusy = { ...this.drawerBusy, deactivate: false };
        if (res.success) {
          this.toast.success('User account deactivated');
          this.loadMembers();
        } else {
          this.toast.error('Could not deactivate account', res.message);
        }
      },
      error: (err) => {
        this.rowBusyId = null;
        this.drawerBusy = { ...this.drawerBusy, deactivate: false };
        this.toast.error('Could not deactivate account', err?.error?.message || 'Please try again.');
      }
    });
  }

  // ---- Fee reminder broadcast ----
  openFeeReminder(): void {
    this.showFeeReminderPanel = true;
    this.reminderChannels = { email: true, sms: true };
    this.reminderMessage = '';
    this.reminderError = '';
  }

  cancelFeeReminder(): void {
    this.showFeeReminderPanel = false;
  }

  sendFeeReminder(): void {
    if (!this.reminderChannels.email && !this.reminderChannels.sms) {
      this.reminderError = 'Select at least one channel.';
      return;
    }
    this.isSendingReminder = true;
    this.reminderError = '';
    this.reminderMessage = '';
    this.paymentService.broadcastOutstandingReminder(this.feeMonth, this.feeYear, this.reminderChannels).subscribe({
      next: (r) => {
        this.isSendingReminder = false;
        if (r?.success && r.data) {
          this.toast.success('Reminders sent', `${r.data.recipients} member(s) - ${r.data.sentEmail} email, ${r.data.sentSms} SMS`);
          this.reminderMessage = `Sent to ${r.data.recipients} member(s).`;
          setTimeout(() => { this.showFeeReminderPanel = false; this.reminderMessage = ''; }, 3500);
        } else {
          this.reminderError = r?.message || 'Could not send reminders.';
        }
      },
      error: (err) => {
        this.isSendingReminder = false;
        this.reminderError = err?.error?.message || 'Could not send reminders.';
      }
    });
  }
}
